import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

import {
  ADMIN_QUERY_KEYS,
  type PixelsResponse,
  type PixelDetailsResponse,
  type PixelValidateResponse,
} from "./adminTypes"

// ---------------------------------------------------------------------------
// Pixels
// ---------------------------------------------------------------------------

export function usePixels() {
  return useQuery<PixelsResponse>({
    queryKey: ADMIN_QUERY_KEYS.pixels,
    queryFn: () => apiFetch<PixelsResponse>("/api/pixels"),
    retry: false,
  })
}

export function usePixelDetails(pixelId: number | null) {
  return useQuery<PixelDetailsResponse>({
    queryKey: ADMIN_QUERY_KEYS.pixelDetails(pixelId ?? 0),
    queryFn: () =>
      apiFetch<PixelDetailsResponse>(`/api/pixels/${pixelId}/details`),
    enabled: pixelId !== null,
    retry: false,
  })
}

export function useValidatePixel() {
  return useMutation({
    mutationFn: (data: { pixel_id: string; access_token: string }) => {
      const fd = new FormData()
      fd.append("pixel_id", data.pixel_id)
      fd.append("access_token", data.access_token)
      return apiFetch<PixelValidateResponse>("/api/pixels/validate", {
        method: "POST",
        body: fd,
      })
    },
  })
}

export function useCreatePixel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      pixel_id: string
      access_token: string
      user_id: number
    }) => {
      const fd = new FormData()
      fd.append("name", data.name)
      fd.append("pixel_id", data.pixel_id)
      fd.append("access_token", data.access_token)
      fd.append("user_id", String(data.user_id))
      return apiFetch<{ success: boolean; message: string }>("/api/pixels", {
        method: "POST",
        body: fd,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pixels })
    },
  })
}

export function useUpdatePixel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      name: string
      pixel_id_value: string
      access_token: string
      user_id: number
    }) => {
      const fd = new FormData()
      fd.append("name", data.name)
      fd.append("pixel_id_value", data.pixel_id_value)
      if (data.access_token) fd.append("access_token", data.access_token)
      fd.append("user_id", String(data.user_id))
      return apiFetch<{ success: boolean; message: string }>(
        `/api/pixels/${data.id}`,
        { method: "PUT", body: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pixels })
    },
  })
}

export function useDeletePixel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pixelId: number) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/pixels/${pixelId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.pixels })
    },
  })
}
